import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, Mail, ArrowRight, Video, MapPin, Clock, Globe2, CalendarClock } from "lucide-react";
import { bookingsApi, BOOKING_TESTIDS as T } from "@/lib/bookingsApi";
import MarketplaceNav from "@/components/coachly/MarketplaceNav";
import SupportLink from "@/components/coachly/SupportLink";
import { formatInTz, isSameTz, localTz, shortModeLabel, tzOffsetLabel, fmtMoney } from "@/lib/sessionFormat";

const POLL_INTERVAL = 2500;
const MAX_POLLS = 12;
const CLIENT_TZ = localTz();

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const bookingId = params.get("booking_id");
  const [rec, setRec] = useState(null);
  const [phase, setPhase] = useState(bookingId ? "polling" : "missing");
  const attempts = useRef(0);

  useEffect(() => {
    if (!bookingId) return;
    let cancelled = false, timer;
    const tick = async () => {
      try {
        const r = await bookingsApi.status(bookingId);
        if (cancelled) return;
        setRec(r.booking);
        if (r.booking.status === "paid" || r.booking.status === "completed") return setPhase("paid");
      } catch {}
      attempts.current += 1;
      if (attempts.current >= MAX_POLLS) return setPhase("timeout");
      timer = setTimeout(tick, POLL_INTERVAL);
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [bookingId]);

  return (
    <div data-testid={T.successPage} className="bg-white min-h-screen">
      <MarketplaceNav />
      <section className="max-w-[900px] mx-auto px-6 md:px-10 py-16 md:py-24">
        {phase === "polling" && (
          <div data-testid={T.successPolling} className="text-center py-20">
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-[#2563EB]" />
            <div className="mt-6 eyebrow">§ Confirming booking</div>
            <h1 className="mt-3 font-display text-3xl md:text-4xl tracking-tight text-slate-900">Just a moment…</h1>
          </div>
        )}
        {phase === "paid" && rec && <Paid rec={rec} />}
        {(phase === "missing" || phase === "timeout") && (
          <div className="text-center py-20">
            <div className="eyebrow">§ {phase === "missing" ? "Missing reference" : "Still processing"}</div>
            <h1 className="mt-3 font-display text-3xl md:text-4xl tracking-tight text-slate-900">
              {phase === "missing"
                ? "No booking id — try again from a coach page."
                : "Payment is taking longer than usual."}
            </h1>
            <div className="mt-8">
              <Link to="/marketplace" className="btn-primary h-11 px-5 rounded-full text-[13px] font-semibold">Back to marketplace</Link>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function Paid({ rec }) {
  const isOnline = rec.mode === "online";
  const coachTz = rec.timezone || "UTC";
  const sameTz = isSameTz(coachTz, CLIENT_TZ);
  const currency = (rec.currency || "usd").toUpperCase();

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
      <div className="inline-flex items-center gap-2 h-8 px-3 rounded-full bg-[#10B981]/10 border border-[#10B981]/30 text-[#059669] text-[12px] font-semibold">
        <Check className="w-3.5 h-3.5" strokeWidth={2.6} /> Booking confirmed
      </div>
      <h1 className="mt-6 font-display font-medium text-[44px] md:text-[76px] leading-[0.95] tracking-[-0.05em] text-slate-900">
        You're booked. <span className="italic font-light text-[#2563EB]">See you then.</span>
      </h1>
      <p className="mt-5 text-slate-600 text-lg max-w-[56ch] leading-relaxed">
        Session with <span className="font-semibold text-slate-900">{rec.coach_name}</span> · a receipt has been emailed to{" "}
        <span className="font-semibold text-slate-900">{rec.client_email}</span>.
      </p>

      {/* Session details card — repeats every important field */}
      <div
        data-testid="booking-success-summary"
        className="mt-10 rounded-2xl border border-slate-200 bg-slate-50 p-6 md:p-7"
      >
        <div className="eyebrow">§ Your session</div>
        <div className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-4">
          <DetailRow Icon={CalendarClock} label="Date & time (your local)" value={
            <span data-testid="booking-success-datetime">
              {formatInTz(rec.session_at, CLIENT_TZ, { weekday: "long" })}
            </span>
          } />
          <DetailRow Icon={Globe2} label="Timezone" value={
            <span data-testid="booking-success-tz">
              {CLIENT_TZ}{tzOffsetLabel(CLIENT_TZ) && <> ({tzOffsetLabel(CLIENT_TZ)})</>}
              {!sameTz && (
                <span className="block text-[12px] text-slate-500 mt-0.5">
                  Coach's local time: {formatInTz(rec.session_at, coachTz)} ({coachTz})
                </span>
              )}
            </span>
          } />
          <DetailRow Icon={isOnline ? Video : MapPin} label="Session type" value={
            <span data-testid="booking-success-mode" className="inline-flex items-center gap-1.5">
              {shortModeLabel(rec.mode)}
              {isOnline && <span className="text-[12px] text-slate-500">· video call link to follow</span>}
            </span>
          } />
          <DetailRow Icon={Clock} label="Duration" value={`${rec.duration_minutes} minutes`} />
        </div>
      </div>

      {/* Money row */}
      <div className="mt-6 grid sm:grid-cols-3 gap-4">
        <Card label="Amount paid">
          <span data-testid={T.successAmount}>{fmtMoney(rec.price_cents, currency)}</span>
        </Card>
        <Card label="Coach receives">
          {fmtMoney(rec.price_cents - rec.application_fee_cents, currency)}
        </Card>
        <Card label="Coachly fee">
          {fmtMoney(rec.application_fee_cents, currency)}
        </Card>
      </div>

      {/* What's next */}
      <div className="mt-10 rounded-2xl border border-slate-200 p-6">
        <div className="eyebrow">§ What's next</div>
        <ol className="mt-4 space-y-3 text-[14px] text-slate-700">
          <NextStep num={1} title="Confirmation email" body="You'll receive a receipt and calendar invite with the exact date and time." />
          {isOnline ? (
            <NextStep num={2} title="Video link" body={`${rec.coach_name} will share the video call link before the session starts.`} />
          ) : (
            <NextStep num={2} title="Meeting details" body={`${rec.coach_name} will confirm the exact meeting spot before the session.`} />
          )}
          <NextStep num={3} title="After the session" body="Once your coach marks the session complete, you'll be able to leave a public review." />
        </ol>
      </div>

      {rec.notification_sent && (
        <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-[13px] text-slate-600 inline-flex items-start gap-2">
          <Mail className="w-4 h-4 mt-0.5 text-slate-500" />
          <span>
            We've logged a confirmation email for <span className="font-semibold text-slate-900">{rec.client_email}</span>.{" "}
            <span className="text-slate-500">(MOCKED — real email delivery ships after we wire an email provider.)</span>
          </span>
        </div>
      )}

      <div className="mt-10 flex flex-wrap gap-3 items-center">
        {rec.status === "completed" ? (
          <Link
            to={`/coach/${rec.coach_slug}?rebook=1#booking-widget`}
            data-testid="book-again-cta"
            className="btn-primary h-12 px-6 rounded-full text-[13.5px] font-semibold inline-flex items-center gap-2"
          >
            Book again with {rec.coach_name.split(" ")[0]}
            <ArrowRight className="w-4 h-4" strokeWidth={2.4} />
          </Link>
        ) : (
          <Link to={`/coach/${rec.coach_slug}`} className="btn-primary h-12 px-6 rounded-full text-[13.5px] font-semibold inline-flex items-center gap-2">
            Back to coach <ArrowRight className="w-4 h-4" strokeWidth={2.4} />
          </Link>
        )}
        <Link to="/marketplace" className="btn-ghost h-12 px-6 rounded-full text-[13.5px] font-semibold">Browse marketplace</Link>
        <SupportLink />
      </div>

      <p className="mt-8 text-[12px] text-slate-500 leading-relaxed max-w-[52ch]">
        Need to reschedule or cancel? Read the{" "}
        <Link to="/refund-policy" className="text-[#2563EB] hover:underline">Refund &amp; Cancellation Policy</Link>{" "}
        or write to{" "}
        <a href="mailto:support@coachly.app" className="text-[#2563EB] hover:underline">support@coachly.app</a>.
      </p>
    </motion.div>
  );
}

function DetailRow({ Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 grid place-items-center text-slate-500 shrink-0">
        <Icon className="w-4 h-4" strokeWidth={2} />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] tracking-[0.22em] uppercase text-slate-500 font-semibold">{label}</div>
        <div className="mt-1 text-[14px] text-slate-900">{value}</div>
      </div>
    </div>
  );
}

function Card({ label, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 p-5">
      <div className="eyebrow">§ {label}</div>
      <div className="mt-2 font-display text-2xl tracking-tight text-slate-900">{children}</div>
    </div>
  );
}

function NextStep({ num, title, body }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 w-6 h-6 rounded-full bg-[#2563EB]/10 text-[#2563EB] text-[12px] font-semibold grid place-items-center shrink-0">
        {num}
      </span>
      <div>
        <div className="font-semibold text-slate-900">{title}</div>
        <p className="text-[13.5px] text-slate-600 leading-relaxed">{body}</p>
      </div>
    </li>
  );
}
